import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import SectionCard from '../../components/admin/SectionCard';
import { EmptyState, LoadingState } from '../../components/admin/StateViews';
import { ImpactStory } from '../../types';
import { ImpactStoryService } from '../../services/ImpactStoryService';
import { useToast } from '../../context/ToastContext';

const PAGE_SIZE = 6;

const ImpactStoryListPage: React.FC = () => {
  const [stories, setStories] = useState<ImpactStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedStory, setSelectedStory] = useState<ImpactStory | null>(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const fetchStories = async () => {
    try {
      setLoading(true);
      const data = await ImpactStoryService.getImpactStories();
      setStories(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch impact stories.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const filteredStories = useMemo(
    () =>
      stories.filter((story) =>
        [story.title, story.story].some((value) =>
          value.toLowerCase().includes(search.toLowerCase())
        )
      ),
    [stories, search]
  );

  const pageCount = Math.max(1, Math.ceil(filteredStories.length / PAGE_SIZE));
  const paginatedStories = filteredStories.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleDeleteStory = async () => {
    if (!selectedStory) {
      return;
    }

    try {
      await ImpactStoryService.deleteImpactStory(selectedStory.id);
      setStories((current) =>
        current.filter((story) => story.id !== selectedStory.id)
      );
      showToast('Impact story deleted.');
    } catch (err) {
      setError('Failed to delete impact story.');
      console.error(err);
    } finally {
      setSelectedStory(null);
    }
  };

  if (loading) {
    return <LoadingState label="Loading impact stories…" />;
  }

  return (
    <AdminPage
      title="Impact Stories"
      description="Editorial content follows the same create/edit flow used across the admin app."
      action={
        <Button variant="contained" onClick={() => navigate('/content/stories/new')}>
          Create Story
        </Button>
      }
    >
      {error ? <Alert severity="error">{error}</Alert> : null}

      <SectionCard
        title="Story library"
        description="Search content, update details, and maintain story freshness."
      >
        <TextField
          label="Search stories"
          fullWidth
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        {filteredStories.length === 0 ? (
          <EmptyState
            title="No stories found"
            description="Create a story or adjust the search query."
            action={
              <Button
                variant="contained"
                onClick={() => navigate('/content/stories/new')}
              >
                Create Story
              </Button>
            }
          />
        ) : (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedStories.map((story) => (
                    <TableRow key={story.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography fontWeight={600}>{story.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {story.story.slice(0, 84)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {new Date(story.updatedAt || story.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            variant="text"
                            onClick={() =>
                              navigate(`/content/stories/${story.id}/edit`)
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            variant="text"
                            color="error"
                            onClick={() => setSelectedStory(story)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {filteredStories.length} stories found
              </Typography>
              <Pagination
                page={page}
                count={pageCount}
                onChange={(_, nextPage) => setPage(nextPage)}
                color="primary"
              />
            </Stack>
          </>
        )}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(selectedStory)}
        title="Delete story"
        description={
          selectedStory
            ? `Are you sure you want to delete "${selectedStory.title}"?`
            : ''
        }
        confirmLabel="Delete"
        confirmColor="error"
        onClose={() => setSelectedStory(null)}
        onConfirm={handleDeleteStory}
      />
    </AdminPage>
  );
};

export default ImpactStoryListPage;
